import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import {
  MainRecipient,
  MessagingType,
  SystemTemplate,
  SystemTemplates,
} from 'models/automation/SystemTemplates';
import { UsageType } from 'models/automation/Common';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { SYSTEM_TEMPLATES_BASE_QUERY_KEY } from './useFetchSystemTemplates';

const useSystemTemplateToggleUtils = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyError } = useNotify();
  const queryKey = [SYSTEM_TEMPLATES_BASE_QUERY_KEY, 'list'];

  const onMutateFactory =
    ({ enabled, keys }: { enabled: boolean; keys: SystemTemplates[] }) =>
    async () => {
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous value
      const previousState =
        queryClient.getQueryData<SystemTemplate[]>(queryKey);

      queryClient.setQueryData<SystemTemplate[]>(
        queryKey,
        (previousTemplates) => {
          const keysNotInTheCache = keys.filter(
            (key) =>
              !previousTemplates?.find((template) => template.key === key),
          );

          return [
            // If the template is not in the cache, then we add it
            ...keysNotInTheCache.map((key) => ({
              agencyUid: '',
              automaticallySent: true,
              enabled,
              htmlFile: '',
              key,
              locales: {},
              mainRecipient: MainRecipient.GUEST,
              messagingType: MessagingType.EMAIL,
              name: '',
              propertyUid: '',
              sendCopyToAgency: false,
              uid: '',
              usageType: UsageType.SYSTEM,
            })),
            // For templates already in the cache, we just update the cache value
            ...(previousTemplates ?? []).map((template) =>
              keys.includes(template.key) ? { ...template, enabled } : template,
            ),
          ];
        },
      );

      return { previousState };
    };

  const onError = (
    error: BaseErrorResponse,
    _: unknown,
    context?: { previousState: SystemTemplate[] | undefined },
  ) => {
    queryClient.setQueryData(queryKey, context?.previousState);

    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.systemTemplates.apiErrorCodes.',
        t,
      }),
    );
  };

  const onSettled = () => {
    queryClient.invalidateQueries(queryKey);
  };

  return {
    onMutateFactory,
    onError,
    onSettled,
  };
};

export default useSystemTemplateToggleUtils;
